import React, { useState } from 'react';
import { Download, FileText, Edit, Brain, Target, BookOpen, Network, GraduationCap } from 'lucide-react';
import { jsPDF } from 'jspdf';

import MathematicsTopicsContainer from './components/MathematicsTopicsContainer';
import HKDSEMaths from './components/HKDSEMaths';
import KnowledgeGraph from './components/KnowledgeGraph';
import PrimaryMathsContainer from './components/PrimaryMathsContainer';
import FloatingChatWindow from './components/FloatingChatWindow';
import ChatWithAIButton from './components/ChatWithAIButton';
import Primary5Topics from './components/Primary5Topics';
import TextSizeControl from './components/TextSizeControl';
import TranslationTrigger from './components/TranslationTrigger';
import TranslationHelper from './components/TranslationHelper';
import { TextSizeProvider } from './contexts/TextSizeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { allMathTopics, mathTopicCategories, getAllTopicsList } from './data/allMathTopics';
import { mathTemplates, mathTemplateTypes, mathUsefulExpressions } from './data/mathTemplates';
// import './utils/mathJaxOverride'; // Disabled to prevent blank content

const App = () => {
  const [activeTab, setActiveTab] = useState('topics');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTopicForFlashCards, setSelectedTopicForFlashCards] = useState(null);
  const [selectedTopicForQuiz, setSelectedTopicForQuiz] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatContext, setChatContext] = useState({});
  const [savedQuizlets, setSavedQuizlets] = useState([]);
  const [savedDrills, setSavedDrills] = useState([]);
  const [templateData, setTemplateData] = useState({
    type: 'proof',
    problem: '',
    given: '',
    toProve: '',
    proof: '',
    conclusion: '',
    formulas: []
  });

  // Get all topics list
  const allTopicsList = getAllTopicsList();
  
  // Filter topics based on search and category
  const filteredTopics = allTopicsList.filter(topic => {
    const matchesCategory = selectedCategory === 'all' || 
      mathTopicCategories.some(cat => cat.id === selectedCategory && cat.topics.includes(topic.id));
    const matchesSearch = topic.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         topic.titleEn.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         topic.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleTemplateChange = (field, value) => {
    setTemplateData(prev => ({ ...prev, [field]: value }));
  };

  // Autofill function to populate wizard with template data
  const autofillFromTemplate = (template) => {
    // Handle both template objects and topic objects
    if (template.modules) {
      // This is a topic object from allMathTopics
      const firstModule = Object.values(template.modules)[0];
      const firstExample = firstModule?.examples?.[0];
      
      setTemplateData({
        type: 'proof', // Default type
        problem: `${template.title} - ${firstModule?.title || ''}`,
        given: firstExample?.question || '',
        toProve: firstExample?.answer || '',
        proof: firstExample?.solution || '',
        conclusion: '',
        formulas: firstExample?.formula ? [firstExample.formula] : []
      });
    } else {
      // This is a template object
      setTemplateData({
        type: template.type || 'proof',
        problem: template.content?.problem || '',
        given: template.content?.given || '',
        toProve: template.content?.toProve || '',
        proof: template.content?.proof || '',
        conclusion: template.content?.conclusion || '',
        formulas: template.formulas || []
      });
    }
    setActiveTab('wizard');
  };

  const generateFlashCardsFromTopic = (topic) => {
    setSelectedTopicForFlashCards(topic);
    setActiveTab('flashcards');
  };

  const startQuizFromTopic = (topic) => {
    setSelectedTopicForQuiz(topic);
    setActiveTab('quiz');
  };

  // Chat functions
  const handleOpenChat = (context) => {
    setChatContext(context);
    setIsChatOpen(true);
  };

  const handleCloseChat = () => {
    setIsChatOpen(false);
    setChatContext({});
  };

  const handleSaveAsQuizlet = (quizletData) => {
    setSavedQuizlets(prev => [...prev, { ...quizletData, id: Date.now() }]);
    console.log('Saved as Quizlet:', quizletData);
  };

  const handleSaveAsDrill = (drillData) => {
    setSavedDrills(prev => [...prev, { ...drillData, id: Date.now() }]);
    console.log('Saved as Drill:', drillData);
  };

  const handleReturnToTopic = () => {
    console.log('Returning to topic:', chatContext);
  };

  // Enhanced PDF generation using jsPDF
  const generatePDF = () => {
    const doc = new jsPDF();
    
    // Set up the document
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - 2 * margin;
    let yPosition = 30;

    // Header
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text(templateData.type.toUpperCase().replace('-', ' ') + ' LETTER/EMAIL', pageWidth / 2, yPosition, { align: 'center' });
    
    yPosition += 20;
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 15;

    // Letter information
    doc.setFontSize(11);
    doc.setFont(undefined, 'normal');
    
    const letterInfo = [
      `Date: ${templateData.date || '[Date]'}`,
      `To: ${templateData.recipient || '[Recipient]'}`,
      `From: ${templateData.sender || '[Sender]'}`,
      `Subject: ${templateData.subject || '[Subject]'}`
    ];

    letterInfo.forEach(info => {
      doc.text(info, margin, yPosition);
      yPosition += 8;
    });

    yPosition += 10;

    // Content sections with different colors/formatting
    const sections = [
      { label: 'Greeting:', content: templateData.greeting || '[Greeting]', color: [0, 100, 200] },
      { label: 'Opening:', content: templateData.opening || '[Opening paragraph]', color: [0, 150, 0] },
      { label: 'Main Body:', content: templateData.mainBody || '[Main body content]', color: [150, 0, 150] },
      { label: 'Conclusion:', content: templateData.conclusion || '[Concluding paragraph]', color: [200, 100, 0] },
      { label: 'Closing:', content: templateData.closing || '[Closing]', color: [200, 0, 0] }
    ];

    sections.forEach(section => {
      // Check if we need a new page
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 30;
      }

      // Section label
      doc.setFont(undefined, 'bold');
      doc.setTextColor(...section.color);
      doc.text(section.label, margin, yPosition);
      yPosition += 8;

      // Section content
      doc.setFont(undefined, 'normal');
      doc.setTextColor(0, 0, 0);
      
      const lines = doc.splitTextToSize(section.content, contentWidth);
      lines.forEach(line => {
        if (yPosition > 280) {
          doc.addPage();
          yPosition = 30;
        }
        doc.text(line, margin, yPosition);
        yPosition += 6;
      });
      
      yPosition += 8;
    });

    // Add footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(`Generated by EssayRight - Page ${i} of ${pageCount}`, pageWidth / 2, 285, { align: 'center' });
    }

    // Save the PDF
    const fileName = `${templateData.type}-letter-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };

  const resetTemplate = () => {
    setTemplateData({
      type: 'proof',
      problem: '',
      given: '',
      toProve: '',
      proof: '',
      conclusion: '',
      formulas: []
    });
  };

  return (
    <LanguageProvider>
      <TextSizeProvider>
        <div className="min-h-screen bg-gray-50">
        {/* Header with Unsplash Background */}
        <div 
          className="relative bg-blue-600 text-white p-8"
          style={{
            backgroundImage: 'linear-gradient(rgba(37, 99, 235, 0.8), rgba(37, 99, 235, 0.8)), url(https://images.unsplash.com/photo-1455390582262-044cdead277a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1973&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="relative z-10 max-w-7xl mx-auto">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-4xl font-bold mb-3">數學右腦 RightMaths</h1>
                <p className="text-blue-100 text-lg">使用AI驅動的數學學習系統，掌握HKDSE數學課程</p>
              </div>
              <div className="hidden md:block">
                <TextSizeControl />
              </div>
            </div>
          </div>
        </div>

      {/* Navigation */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center">
            <nav className="flex space-x-8">
              {[
                { id: 'topics', label: '數學課題', icon: FileText },
                { id: 'primary', label: '小學數學', icon: GraduationCap },
                { id: 'hkdse', label: 'HKDSE', icon: BookOpen },
                { id: 'knowledge', label: '知識圖譜', icon: Network }
              ].map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
            <div className="md:hidden">
              <TextSizeControl />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Mathematics Topics Tab */}
        {activeTab === 'topics' && (
          <MathematicsTopicsContainer
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            categories={[
              { id: 'all', name: '全部課題' },
              ...mathTopicCategories.map(cat => ({ id: cat.id, name: cat.title }))
            ]}
            filteredTopics={filteredTopics}
            autofillFromTemplate={autofillFromTemplate}
            generateFlashCardsFromTopic={generateFlashCardsFromTopic}
            startQuizFromTopic={startQuizFromTopic}
            templateData={templateData}
            handleTemplateChange={handleTemplateChange}
            templateTypes={mathTemplateTypes}
            usefulExpressions={mathUsefulExpressions}
            resetTemplate={resetTemplate}
            setActiveTab={setActiveTab}
            generatePDF={generatePDF}
            selectedTopicForFlashCards={selectedTopicForFlashCards}
            setSelectedTopicForFlashCards={setSelectedTopicForFlashCards}
            selectedTopicForQuiz={selectedTopicForQuiz}
            setSelectedTopicForQuiz={setSelectedTopicForQuiz}
          />
        )}

        {/* Primary Maths Tab */}
        {activeTab === 'primary' && (
          <PrimaryMathsContainer />
        )}

        {/* HKDSE Maths Tab */}
        {activeTab === 'hkdse' && (
          <HKDSEMaths />
        )}

        {/* Knowledge Graph Tab */}
        {activeTab === 'knowledge' && (
          <KnowledgeGraph />
        )}
      </div>

      {/* Global Floating Chat Button */}
      <ChatWithAIButton
        onClick={handleOpenChat}
        context={{ title: 'RightMaths 數學助手', id: 'math-general' }}
        position="floating"
        variant="floating"
        size="large"
      />

      {/* Floating Chat Window */}
      <FloatingChatWindow
        isOpen={isChatOpen}
        onClose={handleCloseChat}
        context={chatContext}
        onSaveAsQuizlet={handleSaveAsQuizlet}
        onSaveAsDrill={handleSaveAsDrill}
        onReturnToTopic={handleReturnToTopic}
      />
      
      {/* Translation Trigger Component */}
      <TranslationTrigger />
      
      {/* Translation Helper Component */}
      <TranslationHelper />
    </div>
      </TextSizeProvider>
    </LanguageProvider>
  );
};

export default App;
